'use client';

import { useState, useCallback, useRef, useEffect, useId } from 'react';
import { useRouter } from 'next/navigation';
import { Search, MapPin, Home, Hash, X, Clock, TrendingUp } from 'lucide-react';
import { cn, slugify, titleCaseCity, formatPrice, generateListingSlug } from '@/lib/utils';
import { suggest, autocomplete, searchListings, type Suggestion } from '@/lib/api';
import { matchCities, COVERED_CITIES } from '@/lib/cities';
import { getRecentSearches, recordSearch, type RecentSearch } from '@/lib/recentSearches';

/** A short marquee list surfaced when the search box is focused but empty. */
const POPULAR_CITIES = ['San Francisco', 'San Jose', 'Oakland', 'Palo Alto', 'Berkeley', 'Sunnyvale'].map(
  (name) => ({ name, slug: slugify(name) })
);

interface SearchBarProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
}

interface CityRow {
  value: string;
  slug: string;
  label: string;
  count?: number;
}

interface ZipRow {
  value: string;
  count?: number;
}

interface QuickHome {
  id: string;
  address: string;
  city: string;
  zip: string;
  mlsListingId: string;
  price: number;
}

/** Slug for a query that names a known city, else null (loaded suggestions first, then the static list). */
function cityMatchSlug(query: string, cities: CityRow[]): string | null {
  const slug = slugify(query);
  if (!slug) return null;
  const hit = cities.find((c) => c.slug === slug);
  if (hit) return hit.slug;
  const covered = COVERED_CITIES.find((c) => slugify(c) === slug);
  return covered ? slugify(covered) : null;
}

function homesCount(n: number | undefined): string | null {
  if (n == null) return null;
  return `${n.toLocaleString()} ${n === 1 ? 'home' : 'homes'}`;
}

export default function SearchBar({
  className,
  size = 'lg',
  placeholder = 'Search by city, ZIP, or address…',
}: SearchBarProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [cities, setCities] = useState<CityRow[]>([]);
  const [zips, setZips] = useState<ZipRow[]>([]);
  const [homes, setHomes] = useState<QuickHome[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recents, setRecents] = useState<RecentSearch[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Flat nav order: cities, then ZIPs, then quick homes.
  const itemCount = cities.length + zips.length + homes.length;

  // "Empty mode": focused with nothing meaningful typed — offer recent searches
  // and popular cities instead of a blank dropdown. Popular cities are always
  // present, so the empty dropdown always has something to show.
  const emptyMode = query.trim().length < 2;
  const emptyCount = recents.length + POPULAR_CITIES.length;
  const navCount = emptyMode ? emptyCount : itemCount;

  // Loaded on focus (not mount) so the empty-state list is fresh each time and
  // we avoid reading localStorage during SSR/hydration.
  const refreshRecents = () => setRecents(getRecentSearches());

  /**
   * Guards against a slow answer for an earlier keystroke landing after a fast
   * answer for the current one. The 200 ms debounce only collapses keystrokes
   * *within* the window — type "pal", pause, type "o alto" and two suggestion
   * requests are genuinely in flight, so without this the dropdown could end up
   * showing results for "pal" while the box reads "palo alto". Every render of
   * the list is gated on the token being the newest one, and the superseded
   * requests are aborted rather than left to finish.
   */
  const suggestSeq = useRef(0);
  const suggestAbort = useRef<AbortController | null>(null);
  useEffect(() => () => suggestAbort.current?.abort(), []);

  const runSuggest = useCallback(
    async (q: string) => {
      const seq = ++suggestSeq.current;
      suggestAbort.current?.abort();
      if (q.length < 2) {
        suggestAbort.current = null;
        setCities([]);
        setZips([]);
        setHomes([]);
        return;
      }
      const controller = new AbortController();
      suggestAbort.current = controller;
      const stale = () => seq !== suggestSeq.current;

      // City-first typed suggestions + quick homes (kept for their full data,
      // so an address row links straight to the listing). They run together;
      // the homes fetch also carries the MLS# case now that mls_listing_id is
      // searchable.
      const [typed, quick] = await Promise.all([
        suggest(q, { signal: controller.signal }).catch(() => null as Suggestion[] | null),
        searchListings({ q, light: true, perPage: 3, page: 0 }, { signal: controller.signal })
          .then((r) =>
            (r.listings ?? []).map((l) => ({
              id: l.id,
              address: l.address,
              city: l.city,
              zip: l.zip,
              mlsListingId: l.mlsListingId,
              price: l.price,
            }))
          )
          .catch(() => [] as QuickHome[]),
      ]);
      if (stale()) return;

      let cityRows: CityRow[];
      let zipRows: ZipRow[];
      if (typed) {
        cityRows = typed
          .filter((s) => s.type === 'city')
          .map((s) => ({
            value: s.value,
            slug: slugify(s.value),
            label: titleCaseCity(s.value),
            count: s.count,
          }));
        zipRows = typed
          .filter((s) => s.type === 'zip')
          .map((s) => ({ value: s.value, count: s.count }));
      } else {
        // /suggest not deployed (or failed): fall back to the static city list
        // plus any city-shaped autocomplete strings, so cities still lead.
        const legacy = await autocomplete(q, { signal: controller.signal }).catch(
          () => [] as string[]
        );
        if (stale()) return;
        const apiCities = quick.length > 0 ? legacy.filter((s) => !/\d/.test(s)) : legacy;
        const names = [
          ...matchCities(q),
          ...apiCities.filter(
            (s) => !matchCities(q).some((c) => c.toLowerCase() === s.toLowerCase())
          ),
        ];
        cityRows = names.map((n) => ({
          value: n,
          slug: slugify(n),
          label: titleCaseCity(n),
        }));
        zipRows = [];
      }

      setCities(cityRows);
      setZips(zipRows);
      setHomes(quick);
      setIsOpen(cityRows.length + zipRows.length + quick.length > 0);
    },
    []
  );

  // Debounce only the *invocation*; the worker above owns the sequence token.
  // The timer lives in a ref so unmounting cancels a pending keystroke instead
  // of letting it fire setState into a gone component.
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);
  const fetchSuggestions = useCallback(
    (q: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => void runSuggest(q), 200);
    },
    [runSuggest]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);
    fetchSuggestions(value);
  };

  const go = (href: string) => {
    setIsOpen(false);
    router.push(href);
  };

  // A city pick applies a clean city FILTER via the URL search params (the
  // results page reads `cities`), NOT a fuzzy text search.
  const handleSelectCity = (city: CityRow) => {
    setQuery(city.label);
    const href = `/homes?cities=${city.slug}`;
    recordSearch({ label: city.label, href, kind: 'city' });
    go(href);
  };

  const handleSelectZip = (zip: ZipRow) => {
    setQuery(zip.value);
    const href = `/homes?zips=${zip.value}`;
    recordSearch({ label: zip.value, href, kind: 'zip' });
    go(href);
  };

  const handleSelectHome = (home: QuickHome) => {
    go(generateListingSlug(home));
  };

  const handleSelectPopular = (city: { name: string; slug: string }) => {
    setQuery(city.name);
    const href = `/homes?cities=${city.slug}`;
    recordSearch({ label: city.name, href, kind: 'city' });
    go(href);
  };

  const handleSelectIndex = (index: number) => {
    if (emptyMode) {
      if (index < recents.length) {
        go(recents[index].href);
      } else {
        const city = POPULAR_CITIES[index - recents.length];
        if (city) handleSelectPopular(city);
      }
      return;
    }
    if (index < cities.length) {
      handleSelectCity(cities[index]);
    } else if (index < cities.length + zips.length) {
      handleSelectZip(zips[index - cities.length]);
    } else {
      const home = homes[index - cities.length - zips.length];
      if (home) handleSelectHome(home);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIndex >= 0 && selectedIndex < navCount) {
      handleSelectIndex(selectedIndex);
      return;
    }
    const q = query.trim();
    if (!q) {
      router.push('/homes');
      return;
    }
    // Typed text that names a city becomes a city filter; anything else
    // (address, MLS#, misc) is a full-text search.
    const slug = cityMatchSlug(q, cities);
    if (slug) {
      const label = titleCaseCity(q);
      const href = `/homes?cities=${slug}`;
      recordSearch({ label, href, kind: 'city' });
      go(href);
    } else {
      const href = `/homes?q=${encodeURIComponent(q)}`;
      recordSearch({ label: q, href, kind: 'query' });
      go(href);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, navCount - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sizeClasses = {
    sm: 'h-10 text-sm',
    md: 'h-12 text-base',
    lg: 'h-14 text-lg',
  };

  const rowClass = (index: number) =>
    cn(
      'flex w-full items-center gap-3 px-4 py-3 text-left text-ink transition-colors',
      index === selectedIndex ? 'bg-emerald-500/15 text-emerald-300' : 'hover:bg-glass'
    );

  // ARIA combobox/listbox wiring: the input owns focus and points at the
  // active option via aria-activedescendant; option buttons are not tab stops.
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const optionId = (index: number) => `${uid}-opt-${index}`;
  const optionProps = (index: number) => ({
    id: optionId(index),
    role: 'option' as const,
    'aria-selected': index === selectedIndex,
    tabIndex: -1,
  });
  const listOpen =
    isOpen && ((emptyMode && emptyCount > 0) || (!emptyMode && itemCount > 0));

  // Keep the keyboard-highlighted option scrolled into view.
  useEffect(() => {
    if (selectedIndex < 0) return;
    document
      .getElementById(`${uid}-opt-${selectedIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, uid]);

  const groupHeader = (label: string) => (
    <li
      role="presentation"
      className="border-t border-hairline px-4 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wider text-ink-faint first:border-t-0"
    >
      {label}
    </li>
  );

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <form onSubmit={handleSubmit} role="search">
        <div className="relative">
          <Search
            className={cn(
              'absolute left-4 top-1/2 -translate-y-1/2 text-ink-faint',
              size === 'lg' ? 'h-5.5 w-5.5' : 'h-5 w-5'
            )}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              refreshRecents();
              setIsOpen(true);
            }}
            placeholder={placeholder}
            aria-label="Search homes"
            role="combobox"
            aria-expanded={listOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={selectedIndex >= 0 ? optionId(selectedIndex) : undefined}
            autoComplete="off"
            className={cn(
              'glass-blur w-full rounded-2xl border border-hairline-strong bg-raised/80 text-ink',
              'pl-12 pr-12 placeholder:text-ink-faint',
              'shadow-lift transition-all focus:border-emerald-500/60 focus:outline-none focus:ring-4 focus:ring-emerald-500/15',
              sizeClasses[size]
            )}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCities([]);
                setZips([]);
                setHomes([]);
                inputRef.current?.focus();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-soft"
              aria-label="Clear search"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </form>

      {/* Empty-state dropdown: recent searches + popular cities. */}
      {isOpen && emptyMode && emptyCount > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Recent searches and popular cities"
          className="glass-blur absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-hairline-strong bg-raised/95 shadow-lift-lg"
        >
          {recents.length > 0 && groupHeader('Recent searches')}
          {recents.map((r, i) => (
            <li key={`recent-${r.href}`} role="presentation">
              <button
                type="button"
                onClick={() => go(r.href)}
                className={rowClass(i)}
                {...optionProps(i)}
              >
                <Clock className="h-5 w-5 flex-shrink-0 text-ink-faint" />
                <span className="min-w-0 flex-1 truncate font-medium">{r.label}</span>
              </button>
            </li>
          ))}
          {groupHeader('Popular cities')}
          {POPULAR_CITIES.map((city, i) => {
            const index = recents.length + i;
            return (
              <li key={`popular-${city.slug}`} role="presentation">
                <button
                  type="button"
                  onClick={() => handleSelectPopular(city)}
                  className={rowClass(index)}
                  {...optionProps(index)}
                >
                  <TrendingUp className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                  <span className="min-w-0 flex-1 truncate font-medium">{city.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Suggestions dropdown: cities first, then ZIPs, then homes. */}
      {isOpen && !emptyMode && itemCount > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Search suggestions"
          className="glass-blur absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-hairline-strong bg-raised/95 shadow-lift-lg"
        >
          {cities.length > 0 && groupHeader('Cities')}
          {cities.map((city, i) => {
            const index = i;
            const count = homesCount(city.count);
            return (
              <li key={`city-${city.slug}`} role="presentation">
                <button
                  type="button"
                  onClick={() => handleSelectCity(city)}
                  className={rowClass(index)}
                  {...optionProps(index)}
                >
                  <MapPin className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                  <span className="min-w-0 flex-1 truncate font-medium">{city.label}</span>
                  {count && <span className="flex-shrink-0 text-sm text-ink-faint">{count}</span>}
                </button>
              </li>
            );
          })}

          {zips.length > 0 && groupHeader('ZIP codes')}
          {zips.map((zip, i) => {
            const index = cities.length + i;
            const count = homesCount(zip.count);
            return (
              <li key={`zip-${zip.value}`} role="presentation">
                <button
                  type="button"
                  onClick={() => handleSelectZip(zip)}
                  className={rowClass(index)}
                  {...optionProps(index)}
                >
                  <Hash className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                  <span className="min-w-0 flex-1 truncate">{zip.value}</span>
                  {count && <span className="flex-shrink-0 text-sm text-ink-faint">{count}</span>}
                </button>
              </li>
            );
          })}

          {homes.length > 0 && groupHeader('Homes')}
          {homes.map((home, i) => {
            const index = cities.length + zips.length + i;
            return (
              <li key={home.id} role="presentation">
                <button
                  type="button"
                  onClick={() => handleSelectHome(home)}
                  className={rowClass(index)}
                  {...optionProps(index)}
                >
                  <Home className="h-5 w-5 flex-shrink-0 text-ink-faint" />
                  <span className="min-w-0 flex-1 truncate">
                    {home.address}, {titleCaseCity(home.city)}
                  </span>
                  <span className="flex-shrink-0 text-sm font-medium text-gold-600">
                    {formatPrice(home.price)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
